import * as React from 'react';
import { cn } from '@/lib/utils';

interface SheetContextValue {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SheetContext = React.createContext<SheetContextValue>({
  open: false,
  onOpenChange: () => {},
});

export interface SheetProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

export function Sheet({ open = false, onOpenChange = () => {}, children }: SheetProps) {
  return <SheetContext.Provider value={{ open, onOpenChange }}>{children}</SheetContext.Provider>;
}

export function SheetTrigger({
  asChild: _asChild,
  children,
}: {
  asChild?: boolean;
  children: React.ReactNode;
}) {
  const { onOpenChange } = React.useContext(SheetContext);
  return (
    <div onClick={() => onOpenChange(true)} className="inline-flex cursor-pointer">
      {children}
    </div>
  );
}

export interface SheetContentProps {
  side?: 'left' | 'right' | 'top' | 'bottom';
  className?: string;
  children: React.ReactNode;
}

export function SheetContent({ side = 'right', className, children }: SheetContentProps) {
  const { open, onOpenChange } = React.useContext(SheetContext);
  if (!open) return null;

  const sideStyles = {
    right: 'right-0 top-0 h-full w-[400px] translate-x-0',
    left: 'left-0 top-0 h-full w-[400px]',
    top: 'top-0 left-0 w-full',
    bottom: 'bottom-0 left-0 w-full',
  };

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/60" onClick={() => onOpenChange(false)} />
      <div
        className={cn(
          'fixed z-50 flex flex-col border-ndd-700 bg-ndd-950 shadow-xl',
          side === 'right' || side === 'left' ? 'border-l' : 'border-t',
          sideStyles[side],
          className
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </>
  );
}

export function SheetHeader({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn('flex flex-col space-y-2 border-b border-ndd-800 p-4', className)}>
      {children}
    </div>
  );
}

export function SheetTitle({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return <h2 className={cn('text-lg font-semibold text-white', className)}>{children}</h2>;
}

export function SheetDescription({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return <p className={cn('text-sm text-ndd-400', className)}>{children}</p>;
}

export function SheetClose({ children }: { children: React.ReactNode }) {
  const { onOpenChange } = React.useContext(SheetContext);
  return <div onClick={() => onOpenChange(false)}>{children}</div>;
}
